import * as CryptoJS from 'crypto-js';
import { iv, key as secKey } from '../services/firebase';
import { ISearchRecord } from '../types/search';

/** Max facilities shown on Enviro Chat home preview cards. */
export const HOMEPAGE_FACILITY_SLOT_COUNT = 3;

export const HOMEPAGE_TOAST_ADDED = 'Facility added to homepage.';
export const HOMEPAGE_TOAST_REMOVED = 'Facility removed from homepage.';
export const HOMEPAGE_TOAST_ORDER_UPDATED = 'Homepage display order updated.';
export const HOMEPAGE_TOAST_ADDING = 'Adding to homepage...';
export const HOMEPAGE_TOAST_REMOVING = 'Removing from homepage...';
export const HOMEPAGE_TOAST_SAVING_ORDER = 'Updating homepage order...';
export const HOMEPAGE_TOAST_SAVE_ERROR = 'Could not save homepage facilities.';

/** First paint on My Facilities — default rows shown in All Facilities. */
export const FACILITIES_INITIAL_PAGE_SIZE = 10;
/** Page size when loading additional facilities after the initial batch. */
export const FACILITIES_NEXT_PAGE_SIZE = 30;

export const parseCoord = (value: unknown): number | undefined => {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
};

export const isValidLatLon = (lat?: number, lon?: number): boolean =>
  typeof lat === 'number' &&
  typeof lon === 'number' &&
  !Number.isNaN(lat) &&
  !Number.isNaN(lon) &&
  !(lat === 0 && lon === 0) &&
  Math.abs(lat) <= 90 &&
  Math.abs(lon) <= 180;

export const decryptGeomString = (encrypted: string): { lat?: number; lon?: number } => {
  if (!encrypted || typeof encrypted !== 'string') return {};
  try {
    const decrypted = CryptoJS.AES.decrypt(encrypted, secKey, {
      iv,
      mode: CryptoJS.mode.CBC,
    }).toString(CryptoJS.enc.Utf8);
    const parts = decrypted.split(',');
    if (parts.length < 2) return {};
    return { lon: parseCoord(parts[0]), lat: parseCoord(parts[1]) };
  } catch {
    return {};
  }
};

/** Same decryption flow as AI Earth / MyTundra plotLatLong */
export const applyPlotLatLong = (record: ISearchRecord): ISearchRecord => {
  const ele: ISearchRecord = {
    ...record,
    location: { ...record.location },
  };

  const geomz =
    (typeof ele.location.geomz === 'string' && ele.location.geomz) ||
    (typeof (ele as any).gm === 'string' && (ele as any).gm) ||
    '';

  if (geomz) {
    const { lat, lon } = decryptGeomString(geomz);
    if (typeof lon === 'number') ele.location.lon = lon;
    if (typeof lat === 'number') ele.location.lat = lat;
  }

  if (!isValidLatLon(ele.location.lat, ele.location.lon) && ele.location.geom_json) {
    const geomJson = ele.location.geom_json as Record<string, Record<string, { geom?: string }>>;
    outer: for (const epnKey of Object.keys(geomJson)) {
      const group = geomJson[epnKey];
      if (!group || typeof group !== 'object') continue;
      for (const innerKey of Object.keys(group)) {
        const element = group[innerKey];
        if (!element?.geom) continue;
        const { lat, lon } = decryptGeomString(element.geom);
        if (isValidLatLon(lat, lon)) {
          ele.location.lat = lat!;
          ele.location.lon = lon!;
          break outer;
        }
      }
    }
  }

  return ele;
};

/** Map API favorite payload to the fields used by list/map preview UIs. */
export const toPreviewFavorite = (raw: Record<string, unknown>): ISearchRecord => {
  const loc = (raw.location as Record<string, unknown>) || {};
  const geomz =
    (typeof loc.geomz === 'string' && loc.geomz) ||
    (typeof (loc as { gm?: string }).gm === 'string' && (loc as { gm?: string }).gm) ||
    (typeof raw.gm === 'string' && raw.gm) ||
    '';

  const base = {
    contentId: String(raw.contentId || raw.content_id || ''),
    entityName: String(raw.entityName || raw.entity_name || ''),
    entityNumber: String(
      raw.entityNumber || raw.entity_number || raw.regulated_entity_number || ''
    ),
    displayName: raw.displayName ? String(raw.displayName) : undefined,
    order: typeof raw.order === 'number' ? raw.order : undefined,
    location: {
      lat: parseCoord(loc.lat) ?? 0,
      lon: parseCoord(loc.lon) ?? parseCoord(loc.lng) ?? 0,
      geomz,
      geom_json: (loc.geom_json as ISearchRecord['location']['geom_json']) || {},
    },
  };

  return applyPlotLatLong(base as ISearchRecord);
};

export const normalizeFavoriteList = (records: ISearchRecord[]): ISearchRecord[] =>
  records.map((record) => applyPlotLatLong(record));

export const getFacilityDisplayLabel = (record: ISearchRecord): string =>
  record.displayName || record.entityName || record.entityNumber || '';

/** Alphabetical order for saved-facility lists — independent of homepage slot order. */
export const sortFavoritesForStableDisplay = (list: ISearchRecord[]): ISearchRecord[] =>
  [...list].sort((a, b) =>
    getFacilityDisplayLabel(a).localeCompare(getFacilityDisplayLabel(b), undefined, {
      sensitivity: 'base',
    })
  );

export const getHomepageFavoriteIds = (
  list: ISearchRecord[],
  slotCount = HOMEPAGE_FACILITY_SLOT_COUNT
): string[] => list.slice(0, slotCount).map((favorite) => favorite.contentId);

export const isHomepageFavorite = (
  list: ISearchRecord[],
  contentId: string,
  slotCount = HOMEPAGE_FACILITY_SLOT_COUNT
): boolean => getHomepageFavoriteIds(list, slotCount).includes(contentId);

export const sortFavoritesByOrder = (list: ISearchRecord[]): ISearchRecord[] =>
  [...list].sort((a, b) => {
    const ao = typeof (a as any).order === 'number' ? (a as any).order : 10000;
    const bo = typeof (b as any).order === 'number' ? (b as any).order : 10000;
    return ao - bo;
  });

export const reorderFavoritesByContentIds = (
  list: ISearchRecord[],
  contentIds: string[]
): ISearchRecord[] => {
  const byId = new Map(list.map((favorite) => [favorite.contentId, favorite]));
  return contentIds
    .map((contentId) => byId.get(contentId))
    .filter((favorite): favorite is ISearchRecord => !!favorite);
};

/** Move a facility into the homepage slots (last slot; bumps prior slot-3 item down). */
export const promoteFavoriteToHomepage = (
  list: ISearchRecord[],
  contentId: string,
  slotCount = HOMEPAGE_FACILITY_SLOT_COUNT
): ISearchRecord[] => {
  const contentIds = list.map((favorite) => favorite.contentId);
  const fromIndex = contentIds.indexOf(contentId);
  if (fromIndex < 0 || fromIndex < slotCount) {
    return list;
  }
  contentIds.splice(fromIndex, 1);
  contentIds.splice(slotCount - 1, 0, contentId);
  return reorderFavoritesByContentIds(list, contentIds);
};

/** Move a homepage-slot facility to the general list (after homepage picks). */
export const demoteFavoriteFromHomepage = (
  list: ISearchRecord[],
  contentId: string,
  slotCount = HOMEPAGE_FACILITY_SLOT_COUNT
): ISearchRecord[] => {
  const contentIds = list.map((favorite) => favorite.contentId);
  const fromIndex = contentIds.indexOf(contentId);
  if (fromIndex < 0 || fromIndex >= slotCount) {
    return list;
  }
  contentIds.splice(fromIndex, 1);
  contentIds.push(contentId);
  return reorderFavoritesByContentIds(list, contentIds);
};

/** Reorder within homepage slots only; rest of the list is unchanged. */
export const reorderHomepageFavorites = (
  list: ISearchRecord[],
  draggedContentId: string,
  targetSlotIndex: number,
  slotCount = HOMEPAGE_FACILITY_SLOT_COUNT
): ISearchRecord[] => {
  const homepage = list.slice(0, slotCount);
  const rest = list.slice(slotCount);
  const homepageIds = homepage.map((favorite) => favorite.contentId);
  const fromIndex = homepageIds.indexOf(draggedContentId);
  if (fromIndex < 0) {
    return list;
  }
  homepageIds.splice(fromIndex, 1);
  const insertAt = Math.min(Math.max(targetSlotIndex, 0), homepageIds.length);
  homepageIds.splice(insertAt, 0, draggedContentId);
  return reorderFavoritesByContentIds(list, [
    ...homepageIds,
    ...rest.map((favorite) => favorite.contentId),
  ]);
};

export const mergeFavoritesByContentId = (
  existing: ISearchRecord[],
  incoming: ISearchRecord[]
): ISearchRecord[] => {
  const merged = new Map<string, ISearchRecord>();
  existing.forEach((favorite) => merged.set(favorite.contentId, favorite));
  incoming.forEach((favorite) => merged.set(favorite.contentId, favorite));
  return sortFavoritesByOrder(Array.from(merged.values()));
};

export type FavoritesListPayload = {
  data?: Record<string, unknown>[];
  totalItems?: number;
};

export const mapFavoritesPayload = (
  payload: FavoritesListPayload | undefined,
  previewOnly: boolean
): { list: ISearchRecord[]; totalItems: number } => {
  const rawList = Array.isArray(payload?.data) ? payload.data : [];
  const list = previewOnly
    ? rawList.map((raw) => toPreviewFavorite(raw))
    : normalizeFavoriteList(rawList as unknown as ISearchRecord[]);
  const totalItems =
    typeof payload?.totalItems === 'number' ? payload.totalItems : list.length;
  return { list, totalItems };
};
